'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { Button as RAC_Button, DialogTrigger as RAC_DialogTrigger } from 'react-aria-components';
import { Popover } from '~/components/Popover';
import styles from './PopTip.module.css';
import { Tooltip } from './Tooltip';
import { TooltipTrigger } from './TooltipTrigger';
type PopTipProps = {
  /**
   * The non-interactive content to display inside the trigger button.
   * ⚠️ Do not pass an element with an `id` attribute — both branches render in
   * the DOM simultaneously (hidden via CSS media query), which would produce
   * duplicate IDs.
   */
  children: ReactNode;
  /** Content shown in the tooltip (desktop) / popover (mobile) */
  content: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  ariaLabel?: string;
};

/**
 * Wraps content in a trigger button so that:
 * - Desktop (hover capable): shows a Tooltip on hover/focus
 * - Mobile (no hover): shows a Popover on tap
 *
 * ⚠️ Both branches render simultaneously (hidden via CSS media query).
 */
function PopTip(t0) {
  const $ = _c(23);
  const {
    children,
    content,
    ariaLabel,
    placement: t1
  } = t0;
  const placement = t1 === undefined ? "top" : t1;
  const t = useTranslations("aria");
  let t2;
  if ($[0] !== children) {
    t2 = <RAC_Button className={styles.triggerButton}>{children}</RAC_Button>;
    $[0] = children;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== content || $[3] !== placement) {
    t3 = <Tooltip placement={placement}>{content}</Tooltip>;
    $[2] = content;
    $[3] = placement;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <span className={styles.desktopOnly}><TooltipTrigger>{t2}{t3}</TooltipTrigger></span>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== children) {
    t5 = <RAC_Button className={styles.triggerButton}>{children}</RAC_Button>;
    $[8] = children;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== ariaLabel || $[11] !== t) {
    t6 = ariaLabel ?? t("more_information");
    $[10] = ariaLabel;
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== content || $[14] !== placement || $[15] !== t6) {
    t7 = <Popover placement={placement} ariaLabel={t6} variant="contrast" hasArrow={true} minimalPadding={true}>{content}</Popover>;
    $[13] = content;
    $[14] = placement;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t5 || $[18] !== t7) {
    t8 = <span className={styles.mobileOnly}><RAC_DialogTrigger>{t5}{t7}</RAC_DialogTrigger></span>;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t4 || $[21] !== t8) {
    t9 = <>{t4}{t8}</>;
    $[20] = t4;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}
export { PopTip };